import { useApi } from "@soft-db/monitoring-api-client";
import {
	checkActionCode,
	indexedDBLocalPersistence,
	initializeAuth,
	inMemoryPersistence,
	signInWithCustomToken,
	signInWithEmailAndPassword,
	signOut,
	updatePassword,
	User,
} from "firebase/auth";
import { defineStore } from "pinia";
import { useFirebaseApp, useIsPublic } from "../plugins";

export const useAuthStore = defineStore("auth-store", () => {
	const api = useApi().auth();

	const router = useRouter();
	const { isPublic, token: publicToken } = useIsPublic();

	const firebaseApp = useFirebaseApp();

	const auth = initializeAuth(firebaseApp, {
		popupRedirectResolver: undefined,
		persistence: isPublic ? [inMemoryPersistence] : [indexedDBLocalPersistence],
	});

	const firebaseUser = shallowRef<User | null | undefined>();
	auth.onAuthStateChanged((user) => {
		firebaseUser.value = user;
	});
	const firebaseLoaded = computed(() => firebaseUser.value !== undefined);

	const isReady = ref(false);

	const isAuth = computed(() => isReady.value && firebaseUser.value != null);

	async function getAuthToken() {
		if (firebaseUser.value == null) {
			return null;
		}

		const token = await firebaseUser.value.getIdToken();
		if (token == null) {
			return null;
		}

		return `mno_${token}`;
	}

	async function init() {
		if (isReady.value) {
			return;
		}

		try {
			if (isPublic && publicToken != null) {
				await publicLogin(publicToken);
			}

			await until(firebaseLoaded).toBeTruthy();
		} catch (e) {
			console.error(e);
			router.push("/auth/login");
		} finally {
			isReady.value = true;
		}
	}

	async function publicLogin(token: string) {
		const { auth_token } = await api.loginPublic(token);
		return await signInWithCustomToken(auth, auth_token);
	}

	async function login(username: string, password: string) {
		const { auth_token } = await api.login(username, password);
		return await signInWithCustomToken(auth, auth_token);
	}

	async function logout() {
		await signOut(auth);

		// Force a full page reload to clear state
		location.href = "/auth/login";
	}

	async function isValidActionCode(code: string) {
		try {
			await checkActionCode(auth, code);
			return true;
		} catch {
			return false;
		}
	}

	async function changePassword(oldPw: string, newPw: string) {
		if (
			firebaseUser.value == null ||
			!firebaseUser.value.email ||
			!firebaseUser.value.emailVerified
		) {
			return;
		}

		try {
			const credUser = await signInWithEmailAndPassword(auth, firebaseUser.value.email, oldPw);
			await updatePassword(credUser.user, newPw);

			return true;
		} catch {
			return false;
		}
	}

	return {
		user: firebaseUser,
		isReady,
		isAuth,
		init,
		login,
		logout,
		getAuthToken,
		isValidActionCode,
		changePassword,
	};
});
